import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createTenant, listPartners } from "../lib/api";
import type { Partner } from "../types";

const WA_CONNECTOR_URL = "https://aibot-wa.jimsbond007.workers.dev";

const PROMPT_TEMPLATES: { label: string; icon: string; prompt: string }[] = [
  {
    label: "客服通用",
    icon: "🎧",
    prompt:
      "你是一位專業的客戶服務代表。請以友善、耐心、簡潔的方式回答客戶問題。如果問題超出你的知識範圍，請禮貌地引導客戶聯繫人工客服。使用繁體中文回覆。",
  },
  {
    label: "銷售顧問",
    icon: "💼",
    prompt:
      "你是一位熱情的銷售顧問。你的目標是了解客戶需求，推薦最適合的產品或服務，並促成交易。語氣要親切專業，適時提出問題以挖掘需求。使用繁體中文回覆。",
  },
  {
    label: "技術支援",
    icon: "🔧",
    prompt:
      "你是一位技術支援工程師。請以清晰、有條理的方式協助用戶解決技術問題。如果問題需要進一步排查，請引導用戶提供相關資訊（如錯誤訊息、操作步驟）。使用繁體中文回覆。",
  },
  {
    label: "餐飲服務",
    icon: "🍽️",
    prompt:
      "你是一間餐廳的親切服務員。請熱情地介紹菜式、回答關於菜單和訂位的問題，並主動推薦招牌菜。語氣要溫暖有禮。使用繁體中文回覆。",
  },
  {
    label: "教育培訓",
    icon: "📚",
    prompt:
      "你是一位耐心的教育顧問。請根據學生或家長的需求，介紹適合的課程和學習方案。回答要詳盡但易懂，鼓勵學習熱情。使用繁體中文回覆。",
  },
  {
    label: "醫療健康",
    icon: "🏥",
    prompt:
      "你是一位專業的醫療諮詢助手。請提供準確、易懂的健康資訊，但必須強調這不能取代專業醫生的診斷和建議。語氣要溫和、專業。使用繁體中文回覆。",
  },
  {
    label: "清空",
    icon: "🧹",
    prompt: "",
  },
];

export default function TenantCreatePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<any>(null);

  const [form, setForm] = useState<Record<string, string | number>>({
    plan_type: "trial",
    trial_quota: 200, // 開站優惠（2026-08-22 拍板 B）：原 100；優惠期後回調
    locale: "zh-HK",
  });
  // 戰略夥伴（P1 §2.4）：揀夥伴＝建租戶即掛 partner_id＋自動套功能白名單
  const [partners, setPartners] = useState<Partner[]>([]);

  useEffect(() => {
    listPartners()
      .then((d) => setPartners(d.partners || []))
      .catch(() => {});
  }, []);

  const handleChange = (field: string, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyTemplate = (prompt: string) => {
    handleChange("system_prompt", prompt);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setResult(null);
    setError("");

    const tenantId = String(form.tenant_id || "").trim();
    if (!tenantId) {
      setError("租戶 ID 為必填");
      setSaving(false);
      return;
    }

    // 移除空值的 api_key_hash，讓後端自動生成
    const payload: Record<string, string | number | null> = {};
    for (const [key, value] of Object.entries(form)) {
      if (key === "api_key_hash" && !value) continue;
      if (value !== "" && value !== undefined) {
        payload[key] = value;
      }
    }

    try {
      const data = await createTenant(payload);
      if (data.success) {
        setResult(data);
      } else {
        setError(data.error || "創建失敗");
      }
    } catch {
      setError("創建失敗");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/tenants")}
          className="px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
        >
          ← 返回
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-100">➕ 新增租戶</h1>
          <p className="text-xs text-slate-500 mt-1">內部／特殊直建（無訂單）；一般客戶付款開通請使用「開通中心」。</p>
        </div>
      </div>

      <div className="max-w-2xl bg-slate-900 rounded-xl border border-slate-800 p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* 租戶 ID */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              租戶 ID <span className="text-rose-400">*</span>
            </label>
            <input
              type="text"
              value={String(form.tenant_id || "")}
              onChange={(e) => handleChange("tenant_id", e.target.value)}
              placeholder="例如：shop_001"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">唯一標識，創建後無法修改。</p>
          </div>

          {/* API Key Hash */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              API Key Hash
              <span className="ml-2 text-xs text-slate-500 font-normal">（留空則自動生成）</span>
            </label>
            <input
              type="text"
              value={String(form.api_key_hash || "")}
              onChange={(e) => handleChange("api_key_hash", e.target.value)}
              placeholder="用於 /ask 接口的 Bearer Token"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
            />
          </div>

          {/* Bot 名稱 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Bot 名稱</label>
            <input
              type="text"
              value={String(form.bot_name || "")}
              onChange={(e) => handleChange("bot_name", e.target.value)}
              placeholder="AI 客服"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* 方案 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">方案</label>
            <select
              value={String(form.plan_type || "trial")}
              onChange={(e) => handleChange("plan_type", e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            >
              <option value="trial">trial — 試用</option>
              <option value="paid">paid — 付費</option>
              <option value="suspended">suspended — 停用</option>
            </select>
          </div>

          {/* 試用額度 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">試用額度</label>
            <input
              type="number"
              min={0}
              value={String(form.trial_quota ?? 200)}
              onChange={(e) => handleChange("trial_quota", Number(e.target.value))}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* 渠道夥伴（P1 §2.4）：揀夥伴＝自動套功能白名單 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">渠道夥伴</label>
            <select
              value={String(form.partner_id || "")}
              onChange={(e) => handleChange("partner_id", e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            >
              <option value="">無夥伴（自營客戶）</option>
              {partners.map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  🤝 {p.name}（{p.partner_id}）
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              揀夥伴＝租戶掛該夥伴（批發結算），並自動套用佢嘅功能白名單——白名單外嘅功能會明確關閉。
            </p>
          </div>

          {/* 引擎提示：由方案層級決定 */}
          <div className="p-4 bg-slate-950/40 border border-slate-700/50 rounded-lg">
            <label className="block text-sm font-medium text-slate-300 mb-1">
              LLM 引擎
            </label>
            <p className="text-sm text-slate-200 mb-2">
              已由方案層級自動決定，毋須手動選擇。
            </p>
            <div className="text-xs text-slate-400 space-y-1">
              <p>
                • <span className="text-emerald-300">Lite</span>：頭兩個月 Premium（DeepSeek v4-flash＋識圖 vision-exp），期滿自動轉 Basic（GLM 免費層）
              </p>
              <p>
                • <span className="text-emerald-300">Pro / Legacy</span>：永久 Premium（DeepSeek 主力）
              </p>
              <p>
                • 詳細規則請見 <a href="#/engine-rules" className="text-sky-400 hover:underline">⚡ 引擎規則</a>
              </p>
            </div>
          </div>

          {/* DeepSeek 自訂 Key（Vision 後備用） */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              DeepSeek 自訂 API Key
              <span className="ml-2 text-xs text-slate-500 font-normal">
                （Vision 後備／留空用系統預設）
              </span>
            </label>
            <input
              type="password"
              value={String(form.llm_api_key || "")}
              onChange={(e) => handleChange("llm_api_key", e.target.value)}
              placeholder="sk-..."
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              一般用系統預設 Key 即可；只有極少數 Vision 後備場景需要自訂。
            </p>
          </div>

          {/* 系統提示詞 */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-300">系統提示詞</label>
              <span className="text-xs text-slate-500">控制 Bot 的性格與行為準則</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {PROMPT_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  type="button"
                  onClick={() => applyTemplate(tpl.prompt)}
                  className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-emerald-600 text-slate-300 hover:text-emerald-400 rounded-lg transition-colors"
                >
                  {tpl.icon} {tpl.label}
                </button>
              ))}
            </div>
            <textarea
              value={String(form.system_prompt || "")}
              onChange={(e) => handleChange("system_prompt", e.target.value)}
              rows={5}
              placeholder="輸入系統提示詞，或點選上方模板..."
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
            />
          </div>

          {/* 人工干預通知手機 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              人工干預通知手機
              <span className="ml-2 text-xs text-slate-500 font-normal">（最多 3 個，用逗號分隔）</span>
            </label>
            <input
              type="text"
              value={String(form.contact_handoff || "")}
              onChange={(e) => handleChange("contact_handoff", e.target.value)}
              placeholder="85291234567, 85298765432, 85251164453"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              當 AI 無法回應或需要轉真人時，系統會發 WhatsApp 通知給這些號碼，並自動暫停 AI 30 分鐘。
            </p>
          </div>

          <hr className="border-slate-700" />

          {/* WhatsApp 配置 */}
          <div>
            <h3 className="text-sm font-semibold text-emerald-400 mb-3">📱 WhatsApp 配置（WhatsApp）</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">WhatsApp API Key</label>
                <input
                  type="password"
                  value={String(form.whatsapp_api_key || "")}
                  onChange={(e) => handleChange("whatsapp_api_key", e.target.value)}
                  placeholder="貼上租戶自己的 RPMSMS API Key"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">發送號碼（Sender Number）</label>
                <input
                  type="text"
                  value={String(form.whatsapp_sender_number || "")}
                  onChange={(e) => handleChange("whatsapp_sender_number", e.target.value)}
                  placeholder="85212345678"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">WTS Webhook Endpoint（留空＝預設 cloudwapi.in）</label>
                <input
                  type="text"
                  value={String(form.rpmsms_base_url || "")}
                  onChange={(e) => handleChange("rpmsms_base_url", e.target.value)}
                  placeholder="https://unofficial.cloudwapi.in"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                />
                <p className="mt-1 text-[11px] text-slate-500">只填 base；留空＝預設 unofficial.cloudwapi.in（2026-09-15 定調）；舊 rpmsms .com 帳號填 https://unofficial.rpmsms.com</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-medium rounded-lg transition-colors"
            >
              {saving ? "創建中..." : "➕ 創建租戶"}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-6 p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
            <p className="text-sm font-medium text-rose-400">❌ 錯誤：{error}</p>
          </div>
        )}

        {result && (
          <div className="mt-6 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-3">
            <p className="text-sm font-medium text-emerald-400">
              ✅ 租戶 {result.tenantId} 創建成功
            </p>
            <div className="text-sm text-slate-300">
              <p className="mb-1">API Key Hash：</p>
              <code className="block p-2 bg-slate-950 rounded text-xs font-mono text-emerald-300 break-all">
                {result.api_key_hash}
              </code>
            </div>
            <div className="text-sm text-slate-300">
              <p className="mb-1">Webhook URL（請複製到 WhatsApp 後台）：</p>
              <code className="block p-2 bg-slate-950 rounded text-xs font-mono text-emerald-300 break-all">
                {result.webhook_url}
              </code>
            </div>
            <button
              onClick={() => navigate(`/tenants/edit/${result.tenantId}`)}
              className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
            >
              ✏️ 繼續編輯租戶
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
